import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import io, { Socket } from 'socket.io-client';
import { backend_url } from '../constants';
import {
  AUTH_TOKEN_REFRESHED_EVENT,
  getAccessToken,
  refreshAuthTokens,
} from '../graphql/client/utils';

const SocketContext = createContext<Socket | null>(null);

export const useSocket = () => useContext(SocketContext);

interface SocketProviderProps {
  children: React.ReactNode;
}

const SOCKET_CONFIG = {
  MAX_RECONNECTION_ATTEMPTS: 5,
  RECONNECTION_DELAY: 1000,
  RECONNECTION_DELAY_MAX: 5000,
  TIMEOUT: 20000,
};

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const isLoggedIn = useSelector((state: any) => state.user.isLoggedIn);
  const [socket, setSocket] = useState<Socket | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const connectionAttemptRef = useRef<number>(0);

  const createSocketInstance = () => {
    connectionAttemptRef.current = 0;

    if (socketRef.current) {
      try {
        socketRef.current.disconnect();
        socketRef.current.close();
      } catch (error) {
        console.error('Error closing previous socket:', error);
      }
    }

    const newSocket = io(backend_url, {
      auth: { token: getAccessToken() },
      reconnectionAttempts: SOCKET_CONFIG.MAX_RECONNECTION_ATTEMPTS,
      reconnectionDelay: SOCKET_CONFIG.RECONNECTION_DELAY,
      reconnectionDelayMax: SOCKET_CONFIG.RECONNECTION_DELAY_MAX,
      timeout: SOCKET_CONFIG.TIMEOUT,
    });

    socketRef.current = newSocket;
    return newSocket;
  };

  const socketInstance = useMemo(() => {
    if (!isLoggedIn) return null;
    return createSocketInstance();
  }, [isLoggedIn]);

  const setupSocketListeners = (socket: Socket) => {
    socket.on('connect', () => {
      connectionAttemptRef.current = 0;
      console.debug('Socket connected successfully');
    });

    socket.on('disconnect', (reason) => {
      console.debug('Socket disconnected:', reason);
      handleDisconnect(socket, reason);
    });

    socket.on('error', (error) => {
      console.error('Socket error:', error);
    });

    socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      if (
        connectionAttemptRef.current < SOCKET_CONFIG.MAX_RECONNECTION_ATTEMPTS
      ) {
        connectionAttemptRef.current++;
      }
    });
  };

  const handleDisconnect = async (socket: Socket, reason: string) => {
    if (reason === 'io server disconnect' || reason === 'transport close') {
      const isUnderMaxAttempts =
        connectionAttemptRef.current < SOCKET_CONFIG.MAX_RECONNECTION_ATTEMPTS;
      if (isUnderMaxAttempts) {
        connectionAttemptRef.current++;
        try {
          console.debug('Refreshing token before reconnection...');
          await refreshAuthTokens();
          const newToken = getAccessToken();
          socket.auth = { token: newToken };
          socket.connect();
        } catch (error) {
          console.error(
            'Error refreshing token for socket reconnection:',
            error,
          );
          socket.auth = { token: getAccessToken() };
          socket.connect();
        }
      }
    }
  };

  const setupTokenRefreshHandler = (socket: Socket) => {
    const handleTokenRefresh = () => {
      if (socket) {
        socket.auth = { token: getAccessToken() };

        try {
          socket.disconnect();
          socket.connect();
        } catch (err) {
          console.error('Error refreshing socket connection:', err);
        }
      }
    };

    window.addEventListener(AUTH_TOKEN_REFRESHED_EVENT, handleTokenRefresh);

    return () => {
      window.removeEventListener(
        AUTH_TOKEN_REFRESHED_EVENT,
        handleTokenRefresh,
      );
    };
  };

  const cleanupSocket = ({
    socket,
    removeTokenRefreshListener,
  }: {
    socket: Socket;
    removeTokenRefreshListener: () => void;
  }) => {
    try {
      removeTokenRefreshListener();

      // Unregister all listeners
      socket.off('connect');
      socket.off('disconnect');
      socket.off('error');
      socket.off('connect_error');

      // Disconnect socket
      socket.disconnect();
    } catch (err) {
      console.error('Error during socket cleanup:', err);
    }
  };

  useEffect(() => {
    if (!socketInstance) return undefined;

    try {
      socketInstance.connect();
      setSocket(socketInstance);

      setupSocketListeners(socketInstance);

      const removeTokenRefreshListener =
        setupTokenRefreshHandler(socketInstance);

      return () => {
        cleanupSocket({
          socket: socketInstance,
          removeTokenRefreshListener,
        });
      };
    } catch (error) {
      console.error('Error in socket setup:', error);
      return undefined;
    }
  }, [socketInstance]);

  return (
    <SocketContext.Provider value={socket}>{children}</SocketContext.Provider>
  );
};
